import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiError, notifyApiError, INR } from "../lib/api";
import { safeCopy } from "../lib/utils";
import { toast } from "sonner";
import { Copy, CheckCircle2 } from "lucide-react";

export default function AddCafeModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    address: "",
    tables_count: 4,
    daily_rate: 8.5,
    initial_paid_days: 7,
    grace_period_days: 3,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        owner_name: "",
        owner_email: "",
        owner_phone: "",
        address: "",
        tables_count: 4,
        daily_rate: 8.5,
        initial_paid_days: 7,
        grace_period_days: 3,
      });
      setResult(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/cafes", {
        ...form,
        tables_count: Number(form.tables_count),
        daily_rate: Number(form.daily_rate),
        initial_paid_days: Number(form.initial_paid_days),
        grace_period_days: Number(form.grace_period_days),
      });
      setResult(data);
      toast.success(`Cafe created · welcome email sent`);
      onCreated && onCreated();
    } catch (err) {
      notifyApiError(toast, err);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    const ok = await safeCopy(text);
    if (ok) toast.message("Copied");
    else toast.error("Couldn't copy — please select & copy manually");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const monthly =
    Number(form.daily_rate || 0) * Number(form.tables_count || 0) * 30;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="add-cafe-modal"
        className="bg-[#121a16] border border-white/10 text-white max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-black">
            {result ? "Cafe Created" : "Onboard New Cafe"}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {result
              ? "Share these credentials with the cafe owner. Welcome email has been sent."
              : "Fill in details — admin & owner accounts are generated automatically."}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Cafe Name
                </Label>
                <Input
                  data-testid="cafe-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Cue Lounge Anna Nagar"
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Owner Name
                </Label>
                <Input
                  required
                  value={form.owner_name}
                  onChange={set("owner_name")}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Owner Phone
                </Label>
                <Input
                  value={form.owner_phone}
                  onChange={set("owner_phone")}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Owner Email (receives credentials)
                </Label>
                <Input
                  required
                  type="email"
                  data-testid="cafe-owner-email-input"
                  value={form.owner_email}
                  onChange={set("owner_email")}
                  placeholder="owner@cafe.com"
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Address
                </Label>
                <Input
                  value={form.address}
                  onChange={set("address")}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Tables
                </Label>
                <Input
                  type="number"
                  min={0}
                  data-testid="cafe-tables-input"
                  value={form.tables_count}
                  onChange={set("tables_count")}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  ₹/table/day
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.daily_rate}
                  onChange={set("daily_rate")}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Trial Days
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={form.initial_paid_days}
                  onChange={set("initial_paid_days")}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Grace Days
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={form.grace_period_days}
                  onChange={set("grace_period_days")}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2 text-xs text-amber-200">
              ≈ {INR(monthly)}/mo at {form.tables_count} tables ×{" "}
              {INR(form.daily_rate)}/day
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                className="text-gray-400 hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="confirm-add-cafe-btn"
                className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest disabled:opacity-60"
              >
                {busy ? "Creating…" : "Create & Email Credentials"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3 mt-2" data-testid="cafe-creds-result">
            <CredsRow
              label="Club (Staff) login"
              email={result.admin_email}
              password={result.admin_password}
              onCopy={copy}
            />
            <CredsRow
              label="Owner (Analytics) login"
              email={result.owner_login_email}
              password={result.owner_password}
              onCopy={copy}
            />
            <div className="text-xs text-gray-500 bg-black/40 border border-white/10 rounded-md px-3 py-2 leading-relaxed">
              <CheckCircle2 className="w-3 h-3 inline -mt-0.5 mr-1 text-emerald-400" />
              Welcome email{" "}
              {result.welcome_email_id ? "sent" : "could not be delivered"} to{" "}
              <span className="text-white font-bold">
                {result.cafe?.owner_email}
              </span>
              . Credentials are also shown above — passwords are <b>not</b> stored
              in plain text, save them now.
            </div>
            <DialogFooter>
              <Button
                onClick={onClose}
                className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CredsRow({ label, email, password, onCopy }) {
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1.5">
        {label}
      </div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <code className="text-amber-300 text-xs font-mono break-all">{email}</code>
        <button
          onClick={() => onCopy(email)}
          className="text-gray-400 hover:text-white"
          title="Copy email"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-center justify-between gap-2">
        <code className="text-white text-xs font-mono break-all">{password}</code>
        <button
          onClick={() => onCopy(password)}
          className="text-gray-400 hover:text-white"
          title="Copy password"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
